import type { TFunction } from 'i18next';

import type { ActivityItem } from '../types';
import { formatMinutes } from '@/shared/utils/time';

type StatusKey = 'TODO' | 'IN_PROGRESS' | 'REVIEW' | 'COMPLETED';
const STATUSES: readonly string[] = ['TODO', 'IN_PROGRESS', 'REVIEW', 'COMPLETED'];

/**
 * Renders a feed entry in the reader's language from its type + params. Free-form entries (comments, attachments,
 * risks) and rows recorded before params existed have no params: their server text is shown as written.
 */
export function activityText(t: TFunction, a: ActivityItem): string {
  const p = a.params ?? {};
  switch (a.type) {
    case 'TASK_CREATED':
    case 'TASK_COMPLETED':
      return p.title ? t(`activity.${a.type}`, { title: p.title }) : a.text;
    case 'TASK_MOVED':
      return p.title && p.status && STATUSES.includes(p.status)
        ? t('activity.TASK_MOVED', { title: p.title, status: t(`status.${p.status as StatusKey}`) })
        : a.text;
    case 'MILESTONE_COMPLETED':
      return p.milestone ? t('activity.MILESTONE_COMPLETED', { milestone: p.milestone }) : a.text;
    case 'PROJECT_CREATED':
      return p.code && p.name ? t('activity.PROJECT_CREATED', { code: p.code, name: p.name }) : a.text;
    case 'TIME_LOGGED': {
      const minutes = Number(p.minutes);
      if (!p.minutes || Number.isNaN(minutes)) return a.text;
      const duration = formatMinutes(minutes);
      return p.source === 'timer' ? t('activity.TIME_LOGGED_timer', { duration }) : t('activity.TIME_LOGGED_manual', { duration });
    }
    case 'MEMBER_JOINED':
      return p.name ? t('activity.MEMBER_JOINED', { name: p.name }) : a.text;
    default:
      return a.text;
  }
}

/** Entries without an actor were written by the system. */
export function activityActor(t: TFunction, a: ActivityItem): string {
  return a.actorId ? a.actorName : t('activity.system');
}
